import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Select from 'react-select';
import { physicalSitesApi, peopleApi } from '../services/api';
import { LinkedArtefacts } from '../components/LinkedArtefacts';
import { useToast } from '../hooks/useToast';
import { PageHeader } from '../components/PageHeader';
import { reactSelectStyles } from '../utils/reactSelectStyles';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type PhysicalSite = {
  siteId: string;
  name?: string;
  locationType?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  stateProvince?: string;
  postCode?: string;
  country?: string;
  primaryContactId?: string | null;
  notes?: string;
};

type Person = {
  personId: string;
  firstName?: string;
  lastName?: string;
  personName?: string;
};

const LOCATION_TYPES = [
  'Headquarters',
  'Regional Office',
  'Branch Office',
  'Data Center',
  'Co-location Facility',
  'Server Room',
  'Warehouse',
  'Distribution Center',
  'Manufacturing Plant',
  'Research & Development Lab',
  'Disaster Recovery Site',
  'Retail Store',
  'Call Center',
  'Training Facility',
];

const EMPTY_FORM = {
  name: '',
  locationType: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  stateProvince: '',
  postCode: '',
  country: '',
  primaryContactId: '',
  notes: '',
};

const getPersonLabel = (p: Person): string => {
  const full = `${p.firstName || ''} ${p.lastName || ''}`.trim();
  return (full || p.personName || p.personId).trim();
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const PhysicalSiteFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const isEdit = Boolean(id);

  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [activeTab, setActiveTab] = useState<'details' | 'artefacts'>('details');

  const { data: people = [] } = useQuery<Person[]>({
    queryKey: ['people'],
    queryFn: peopleApi.getAll,
  });

  // Load existing site when editing
  const { data: siteData, isLoading: loadingSite } = useQuery<any>({
    queryKey: ['physicalSite', id],
    queryFn: () => physicalSitesApi.getOne(id!),
    enabled: isEdit,
  });

  useEffect(() => {
    if (siteData) {
      const site: PhysicalSite = siteData.physicalSite ?? siteData;
      setForm({
        name: site.name || '',
        locationType: site.locationType || '',
        addressLine1: site.addressLine1 || '',
        addressLine2: site.addressLine2 || '',
        city: site.city || '',
        stateProvince: site.stateProvince || '',
        postCode: site.postCode || '',
        country: site.country || '',
        primaryContactId: site.primaryContactId || '',
        notes: site.notes || '',
      });
    }
  }, [siteData]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        ...form,
        primaryContactId: form.primaryContactId || null,
      };
      if (isEdit) {
        await physicalSitesApi.update(id!, payload);
      } else {
        await physicalSitesApi.create(payload);
      }
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['physicalSites'] });
      toast.success(isEdit ? 'Site updated.' : 'Site created.');
      navigate('/physical-sites');
    },
    onError: (e: Error) => toast.error(`Failed to save: ${e.message}`),
  });

  if (isEdit && loadingSite) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-map-pin-2-line"
        title={isEdit ? 'Edit Physical Site' : 'Add Physical Site'}
        colour="warning"
      />

      {/* Tab navigation */}
      <div className="np-tabs">
        <button
          type="button"
          className={`np-tab d-flex align-items-center gap-1${activeTab === 'details' ? ' active' : ''}`}
          onClick={() => setActiveTab('details')}
        >
          <i className="ri-building-line"></i>Site Details
        </button>
        <button
          type="button"
          className={`np-tab d-flex align-items-center gap-1${activeTab === 'artefacts' ? ' active' : ''}`}
          onClick={() => setActiveTab('artefacts')}
        >
          <i className="ri-links-line"></i>Linked Artefacts
        </button>
      </div>

      {/* ── SITE DETAILS ── */}
      {activeTab === 'details' && (
        <div className="np-card">
          <div className="np-card-head">
            <h2><i className="ri-map-pin-2-line text-warning"></i> Location Information</h2>
          </div>
          <div className="np-card-body">
            <div className="row g-3">
              <div className="col-md-8 np-field">
                <label>
                  Location Name <span className="text-danger">*</span>
                </label>
                <input
                  className="np-input"
                  placeholder="e.g. London Headquarters"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
              </div>

              <div className="col-md-4 np-field">
                <label>Location Type</label>
                <select
                  className="np-select"
                  value={form.locationType}
                  onChange={(e) => setForm((f) => ({ ...f, locationType: e.target.value }))}
                >
                  <option value="">Select type…</option>
                  {LOCATION_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div className="col-12 np-field">
                <label>Address Line 1</label>
                <input
                  className="np-input"
                  placeholder="Street address"
                  value={form.addressLine1}
                  onChange={(e) => setForm((f) => ({ ...f, addressLine1: e.target.value }))}
                />
              </div>

              <div className="col-12 np-field">
                <label>Address Line 2</label>
                <input
                  className="np-input"
                  placeholder="Suite, floor, building (optional)"
                  value={form.addressLine2}
                  onChange={(e) => setForm((f) => ({ ...f, addressLine2: e.target.value }))}
                />
              </div>

              <div className="col-md-6 np-field">
                <label>City</label>
                <input
                  className="np-input"
                  placeholder="City"
                  value={form.city}
                  onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
                />
              </div>

              <div className="col-md-6 np-field">
                <label>State / Province</label>
                <input
                  className="np-input"
                  placeholder="State or Province"
                  value={form.stateProvince}
                  onChange={(e) => setForm((f) => ({ ...f, stateProvince: e.target.value }))}
                />
              </div>

              <div className="col-md-4 np-field">
                <label>Post Code / ZIP</label>
                <input
                  className="np-input"
                  placeholder="Post code"
                  value={form.postCode}
                  onChange={(e) => setForm((f) => ({ ...f, postCode: e.target.value }))}
                />
              </div>

              <div className="col-md-8 np-field mb-0">
                <label>Country</label>
                <input
                  className="np-input"
                  placeholder="Country"
                  value={form.country}
                  onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'details' && (
        <div className="np-card">
          <div className="np-card-head">
            <h2><i className="ri-user-line text-info"></i> Contact &amp; Notes</h2>
          </div>
          <div className="np-card-body">
            <div className="row g-3">
              <div className="col-md-6 np-field">
                <label>Primary Contact</label>
                <Select
                  options={people.map((p) => ({ value: p.personId, label: getPersonLabel(p) }))}
                  value={
                    form.primaryContactId
                      ? (() => {
                          const p = people.find((x) => x.personId === form.primaryContactId);
                          return p ? { value: p.personId, label: getPersonLabel(p) } : null;
                        })()
                      : null
                  }
                  onChange={(opt) => setForm((f) => ({ ...f, primaryContactId: opt?.value ?? '' }))}
                  placeholder="Select person…"
                  isClearable
                  isSearchable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={reactSelectStyles()}
                />
              </div>

              <div className="col-12 np-field mb-0">
                <label>Notes</label>
                <textarea
                  className="np-input"
                  rows={3}
                  placeholder="Any additional notes about this location…"
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── LINKED ARTEFACTS ── */}
      {activeTab === 'artefacts' && (
        <div className="np-card">
          <div className="np-card-head">
            <h2><i className="ri-links-line text-primary"></i> Linked Artefacts</h2>
          </div>
          <div className="np-card-body">
            {isEdit ? (
              <>
                <p className="np-sub mb-3">
                  <i className="ri-information-line me-1"></i>
                  Link legal requirements, regulations, controls or other artefacts to this physical site.
                  Useful for tracking which regulations apply to specific operating locations (e.g. GDPR
                  data residency, local health &amp; safety law, building permits).
                </p>
                <LinkedArtefacts
                  artefactId={id!}
                  artefactType="physical_site"
                  artefactRegister="physical-sites"
                />
              </>
            ) : (
              <div className="text-center py-5 np-sub">
                <i className="ri-links-line ri-3x d-block mb-3 opacity-25"></i>
                <p className="mb-1 fw-semibold">Linked Artefacts not yet available</p>
                <p className="small mb-0">Save this site first, then come back to link legal requirements, regulations, controls and other artefacts.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Save / Cancel */}
      {activeTab === 'details' && (
        <div className="d-flex gap-2 justify-content-end mt-3">
          <button
            type="button"
            className="np-btn np-btn-ghost"
            onClick={() => navigate('/physical-sites')}
          >
            <i className="ri-close-line"></i>Cancel
          </button>
          <button
            type="button"
            className="np-btn np-btn-primary"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || !form.name.trim()}
          >
            {saveMutation.isPending ? (
              <><span className="spinner-border spinner-border-sm"></span>Saving…</>
            ) : isEdit ? (
              'Save Changes'
            ) : (
              'Create Site'
            )}
          </button>
        </div>
      )}

      {activeTab === 'artefacts' && (
        <div className="d-flex gap-2 justify-content-end mt-3">
          <button
            type="button"
            className="np-btn np-btn-ghost"
            onClick={() => navigate('/physical-sites')}
          >
            <i className="ri-arrow-left-line"></i>Back to Sites
          </button>
        </div>
      )}
    </div>
  );
};
